import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import usePaymentMethods from '@hooks/usePaymentMethods'
import { generateUrls } from '@hooks/usePaymentMethod'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PaymentMethodListItem from '../PaymentMethodListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreatePaymentMethodModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const paymentMethodsPayload = usePaymentMethods({
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPaymentMethods,
    filteredPaymentMethodsCount,
    hasFilteredPaymentMethods,
    loading,
  } = paymentMethodsPayload

  const { paymentMethodsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Payment Methods | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreatePaymentMethodModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="paymentMethods"
        tabBarItems={[
          {
            href: paymentMethodsIndexUrl,
            icon: faGlobe,
            key: 'paymentMethods',
            title: 'Payment Methods',
          },
        ]}
        title="Payment Methods"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredPaymentMethods && <ListBlankState />}

            {!loading && hasFilteredPaymentMethods && (
              <DashboardModule title="Payment Methods">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredPaymentMethods.map(paymentMethod => (
                      <PaymentMethodListItem key={paymentMethod.id} paymentMethod={paymentMethod} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredPaymentMethodsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
